import { createClientLogger, logClientError } from '@/client/logging/client-logger';
import { useCallback } from 'react';
import { toast } from '@/ui/toast';
import { showSelectionLimitReachedToast } from '@/ui/selection-limit-toast';
import { toggleAllSelectionWithLimit } from '@/components/group-tables/selection-state';

type UseSelectionGroupActionArgs = {
    hasPickedItems: boolean;
    allItemIds: string[];
    maxSelectedItems: number | null;
    setPickedItems?: (val: string[]) => void;
    clearSelection?: () => Promise<unknown>;
    setSelection?: (ids: string[]) => Promise<unknown>;
    clearSelectionErrorMessage?: string;
    setSelectionErrorMessage?: string;
};

type UseSelectionGroupActionResult = {
    onAllPickedItems: () => Promise<void>;
};

const selectionGroupLogger = createClientLogger({
    scope: 'selection-group',
});

export const useSelectionGroupAction = ({
    hasPickedItems,
    allItemIds,
    maxSelectedItems,
    setPickedItems,
    clearSelection,
    setSelection,
    clearSelectionErrorMessage = 'Не удалось снять выделение',
    setSelectionErrorMessage = 'Не удалось выделить группы',
}: UseSelectionGroupActionArgs): UseSelectionGroupActionResult => {
    const onAllPickedItems = useCallback(async () => {
        if (!setPickedItems) {
            return;
        }

        const selectionResult = toggleAllSelectionWithLimit({
            hasPickedItems,
            allItemIds,
            maxSelectedItems,
        });

        if (selectionResult.mode === 'clear') {
            if (!clearSelection) {
                setPickedItems([]);
                return;
            }

            try {
                await clearSelection();
                setPickedItems([]);
            } catch (error) {
                logClientError({
                    logger: selectionGroupLogger,
                    message: '[client.group-management.selection-clear] Failed to clear selected groups.',
                    error,
                    context: {
                        allItems: allItemIds.length,
                    },
                });
                toast.error(clearSelectionErrorMessage);
            }

            return;
        }

        if (selectionResult.mode === 'noop') {
            if (selectionResult.isSelectionLimitReached && maxSelectedItems !== null) {
                showSelectionLimitReachedToast({ maxSelectedItems, scope: 'groups' });
            }
            return;
        }

        if (setSelection) {
            try {
                await setSelection(selectionResult.nextPickedIds);
                setPickedItems(selectionResult.nextPickedIds);
            } catch (error) {
                logClientError({
                    logger: selectionGroupLogger,
                    message: '[client.group-management.selection-persist] Failed to persist selected groups.',
                    error,
                    context: {
                        allItems: allItemIds.length,
                        selectedItems: selectionResult.nextPickedIds.length,
                    },
                });
                toast.error(setSelectionErrorMessage);
                return;
            }
        } else {
            setPickedItems(selectionResult.nextPickedIds);
        }

        if (selectionResult.isSelectionLimitReached && maxSelectedItems !== null) {
            showSelectionLimitReachedToast({ maxSelectedItems, scope: 'groups' });
        }
    }, [
        allItemIds,
        clearSelection,
        clearSelectionErrorMessage,
        hasPickedItems,
        maxSelectedItems,
        setPickedItems,
        setSelection,
        setSelectionErrorMessage,
    ]);

    return {
        onAllPickedItems,
    };
};
